import type { ApiError } from '@porotutu/react/errors'
import {
  type QueryKey,
  type UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { BellIcon } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { notifyFailed } from '@/components/notify'
import { useCount } from '@/session/useCount'
import { useSession, useT } from '@/session/useSession'
import { IconButton } from '../IconButton/IconButton'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Popover, PopoverContent, PopoverTitle, PopoverTrigger } from '../ui/popover'
import { Inbox, type InboxEntry, type InboxPage } from './Inbox'
import styles from './NotificationBell.module.css'

export type { InboxEntry, InboxPage }

export type NotificationBellProps<Key extends QueryKey = QueryKey, Kind extends string = string> = {
  inboxQuery: UseQueryOptions<InboxPage<Kind>, Error, InboxPage<Kind>, Key>
  markAllRead: () => Promise<unknown>
  invalidates: readonly unknown[]
  entryLink: (entry: InboxEntry<Kind>, label: string, close: () => void) => ReactNode
}

export function NotificationBell<Key extends QueryKey, Kind extends string>({
  inboxQuery,
  markAllRead,
  invalidates,
  entryLink,
}: NotificationBellProps<Key, Kind>) {
  const t = useT()
  const countOf = useCount()
  const { locale } = useSession()
  const [open, setOpen] = useState(false)
  const queryClient = useQueryClient()
  const inbox = useQuery(inboxQuery)

  const markRead = useMutation<unknown, ApiError>({
    mutationFn: markAllRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: invalidates }),
    onError: (error) => notifyFailed(t, error),
  })

  const unread = inbox.data?.meta.unread ?? 0
  const count = unread > 9 ? '9+' : String(unread)

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <IconButton
            className={styles.trigger}
            label={unread > 0 ? countOf('notifications.unread', unread) : t('notifications.toggle')}
          >
            <BellIcon />
            {unread > 0 ? (
              <Badge variant="destructive" aria-hidden className={styles.count}>
                {count}
              </Badge>
            ) : null}
          </IconButton>
        }
      />
      <PopoverContent align="end" className={styles.panel}>
        <PopoverTitle>{t('notifications.heading')}</PopoverTitle>

        <Inbox
          inbox={inbox}
          locale={locale}
          entryLink={(entry, label) => entryLink(entry, label, () => setOpen(false))}
        />

        {unread > 0 ? (
          <Button size="sm" disabled={markRead.isPending} onClick={() => markRead.mutate()}>
            {t('notifications.mark_all_read')}
          </Button>
        ) : null}
      </PopoverContent>
    </Popover>
  )
}
